// email-step v1: 郵箱輸入步驟組件
// 用於忘記密碼流程的第一步：輸入郵箱並發送驗證碼

import React, { useState } from 'react';

import { sendOTP } from '../../../services/supabase/auth';

/**
 * EmailStep 組件 - 郵箱輸入步驟
 * @param {Object} props - 組件屬性
 * @param {string} props.email - 郵箱地址
 * @param {Function} props.onEmailChange - 郵箱變化處理函數
 * @param {Function} props.onNextStep - 進入下一步的回調函數
 * @param {string} props.error - 錯誤信息
 * @param {Function} props.setError - 設置錯誤的函數
 * @param {boolean} props.isLoading - 是否正在加載
 * @param {Function} props.setIsLoading - 設置加載狀態的函數
 * @returns {JSX.Element} 郵箱輸入步驟組件
 */
const EmailStep = ({
  email,
  onEmailChange,
  onNextStep,
  error,
  setError,
  isLoading,
  setIsLoading,
}) => {
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeSentSuccessfully, setCodeSentSuccessfully] = useState(false); // 跟踪验证码是否发送成功

  // 驗證郵箱格式
  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 處理發送驗證碼
  const handleSendCode = async () => {
    if (!email) {
      setError('email', 'Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('email', 'Please enter a valid email address');
      return;
    }

    setIsSendingCode(true);
    setError('email', ''); // 清除之前的錯誤

    try {
      const result = await sendOTP(email);

      if (result.success) {
        // 發送成功，標記為已發送，但不自動進入下一步
        setIsSendingCode(false);
        setCodeSentSuccessfully(true);
        setError('email', ''); // 清除錯誤
      } else {
        // 處理錯誤
        setIsSendingCode(false);
        let errorMessage = 'Failed to send verification code, please try again';

        if (result.error) {
          // 處理冷却時間錯誤
          if (
            result.error.includes('cooldown') ||
            result.error.includes('429') ||
            result.error.includes('wait') ||
            result.error.includes('Please wait')
          ) {
            const remaining = result.remaining || 600;
            const minutes = Math.floor(remaining / 60);
            const seconds = remaining % 60;
            if (minutes > 0) {
              errorMessage = `Please wait ${minutes} minute${
                minutes > 1 ? 's' : ''
              }${
                seconds > 0
                  ? ` and ${seconds} second${seconds > 1 ? 's' : ''}`
                  : ''
              } before requesting another code.`;
            } else {
              errorMessage = `Please wait ${seconds} second${
                seconds > 1 ? 's' : ''
              } before requesting another code.`;
            }
          } else if (
            result.error.includes('not found') ||
            result.error.includes('404')
          ) {
            errorMessage = 'Email not found. Please check your email address';
          } else {
            errorMessage = result.error;
          }
        }

        setError('email', errorMessage);
      }
    } catch (error) {
      console.error('Failed to send reset code:', error);
      setIsSendingCode(false);
      setError('email', 'Failed to send verification code, please try again');
    }
  };

  // 處理第一個繼續按鈕（需要先發送驗證碼）
  const handleContinueAfterSend = () => {
    if (!email) {
      setError('email', 'Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('email', 'Please enter a valid email address');
      return;
    }

    if (!codeSentSuccessfully) {
      setError(
        'email',
        'Please click "Send Code" first to receive a verification code'
      );
      return;
    }

    // 驗證碼已發送成功，進入下一步
    onNextStep();
  };

  // 處理第二個繼續按鈕（已收到驗證碼，直接進入）
  const handleContinueWithExistingCode = () => {
    if (!email) {
      setError('email', 'Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('email', 'Please enter a valid email address');
      return;
    }

    // 直接進入下一步（用戶已經有驗證碼）
    onNextStep();
  };

  return (
    <div className='space-y-4'>
      {/* 說明文字 */}
      <div className='text-center'>
        <p className='text-sm text-gray-600'>
          Enter your email address and we'll send you a verification code to
          reset your password.
        </p>
      </div>

      {/* 郵箱輸入框 */}
      <div>
        <label
          htmlFor='resetEmail'
          className='block text-sm font-medium text-gray-700 mb-2'
        >
          Student Email
        </label>
        <div className='flex space-x-2'>
          <div className='relative flex-1'>
            <input
              id='resetEmail'
              type='email'
              value={email}
              onChange={e => onEmailChange(e.target.value)}
              placeholder='Enter your email'
              className={`block w-full px-3 py-3 border rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-tag-blue focus:border-tag-blue sm:text-sm ${
                error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
          </div>
          <button
            type='button'
            onClick={handleSendCode}
            disabled={isSendingCode || !email}
            className={`px-4 py-3 text-sm font-medium rounded-md whitespace-nowrap transition-colors duration-200 ${
              email && !isSendingCode
                ? 'text-white bg-tag-blue hover:bg-tag-dark-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tag-blue'
                : 'text-gray-400 bg-gray-200 cursor-not-allowed'
            }`}
          >
            {isSendingCode ? 'Sending...' : 'Send Code'}
          </button>
        </div>
        {error && <p className='mt-1 text-sm text-red-600'>{error}</p>}
        {codeSentSuccessfully && (
          <p className='mt-1 text-sm text-green-600'>
            ✓ Verification code has been sent to your email
          </p>
        )}
      </div>

      {/* 操作按鈕 */}
      <div className='flex flex-col space-y-2 pt-4'>
        {/* 第一個 Continue：需要先發送驗證碼 */}
        <button
          type='button'
          onClick={handleContinueAfterSend}
          disabled={!email || !codeSentSuccessfully || isLoading}
          className='flex-1 bg-tag-blue text-white py-3 px-4 rounded-md text-sm font-medium hover:bg-tag-dark-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-tag-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200'
        >
          Continue
        </button>

        {/* 第二個 Continue：已收到驗證碼，直接進入 */}
        <button
          type='button'
          onClick={handleContinueWithExistingCode}
          disabled={!email || isLoading}
          className='flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-md text-sm font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200'
        >
          Continue (I already have a code)
        </button>
      </div>
    </div>
  );
};

export default EmailStep;
